import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { useMatrixClient } from '../../context/MatrixContext';
import { useDisambiguation } from '../../context/DisambiguationContext';
import ContactList from '../ContactList';
import RightSidebar from './RightSidebar';
import Trans from '../Trans';
import FancyTimeStamp from '../FancyTimeStamp';
import useMessageDetails from '../../hooks/useMessageDetails';
import { TIMESTAMP_THRESHOLDS } from '../../constants';
import logger from '../../utils/logger';
import { getRedactionInfo } from '../../utils/redactionUtils';
import { formatFileSize, getFileType } from '../../utils/fileUtils';
import './RightSidebar.css';
import './MessageDetails.css';

/**
 * MessageDetails component displays detailed information about a specific message
 * including sender, timestamp, message ID, and read status by room members
 */
const MessageDetails = ({
  message,
  members,
  roomId,
  onClose,
  onContactClick,
}) => {
  const { t } = useI18n();
  const client = useMatrixClient();
  const { needsDisambiguation } = useDisambiguation();
  const [showReadBy, setShowReadBy] = useState(true);
  const [showNotReadBy, setShowNotReadBy] = useState(true);
  const [redactionEvent, setRedactionEvent] = useState(null);

  // Extract all message details logic to custom hook
  const {
    messageId,
    timestamp,
    senderDisplayName,
    readByMembers,
    notReadByMembers,
    isAttachment,
    attachmentName,
    attachmentSize,
    attachmentMimeType,
    isBadEncrypted,
    isCurrentUserMessage,
    isFailedSend,
    messageContent,
  } = useMessageDetails(message, members, roomId);

  // Fetch redaction event if message is redacted
  useEffect(() => {
    if (message && message.isRedacted && message.isRedacted()) {
      const redactionEventId = message.getUnsigned()?.redacted_because?.event_id;
      if (redactionEventId && client) {
        try {
          const room = client.getRoom(roomId);
          const redactEvent = room?.findEventById(redactionEventId);
          setRedactionEvent(redactEvent);
          logger.debug('[MessageDetails] Found redaction event:', redactEvent);
        } catch (err) {
          logger.warn('[MessageDetails] Could not find redaction event', err);
        }
      }
    }
  }, [message, client, roomId]);

  const handleReadByHeaderClick = () => {
    if ((readByMembers.length) > 0) setShowReadBy(!showReadBy);
  };

  const handleNotReadByHeaderClick = () => {
    if ((notReadByMembers.length) > 0) setShowNotReadBy(!showNotReadBy);
  };

  const shouldRenderInfoBlock = () => !isBadEncrypted;
  const shouldRenderReadByBlock = () => !isBadEncrypted && !isFailedSend;
  const shouldRenderNotReadByBlock = () => !isBadEncrypted && !isFailedSend;
  const shouldRenderRedactionBlock = () => !isBadEncrypted;
  const shouldRenderErrorBlock = () => isBadEncrypted || isFailedSend;

  // Calculate consistent timestamp format based on message age
  // This ensures "Sent by" and "Deleted on" use the same format strategy
  // We always use eventTime (lll format) or buildTime (llll format) to avoid
  // the dynamic "X min ago" / "Yesterday" behavior of the short prop
  const getTimestampFormatProps = () => {
    const now = Date.now();
    const timeDiff = now - timestamp;
    if (timeDiff < TIMESTAMP_THRESHOLDS.NINE_MONTHS_MS) {
      // Recent messages (< 9 months) - show format like "Nov 5, 2024 11:26 PM"
      return { eventTime: true };
    } else {
      // Old messages - show full format like "Tuesday, November 5, 2024 11:26 PM"
      return { buildTime: true };
    }
  };

  const timestampProps = getTimestampFormatProps();

  const renderInfoBlock = () => (
    <div className="message-details-block">
      <div className="message-details-block-item">
        {isAttachment && (
          <div className="message-details-attachment">
            <span className="message-details-attachment-name">{attachmentName}</span>
            {attachmentSize > 0 && (
              <span className="message-details-attachment-info">
                {getFileType(attachmentMimeType, attachmentName)} · {formatFileSize(attachmentSize)}
              </span>
            )}
          </div>
        )}
        <span className="message-details-date">
          <Trans
            i18nKey={isFailedSend ? 'MessageDetailsView.attemptedToSendByXon' : 'MessageDetailsView.sentByXon'}
            defaults={isFailedSend ? 'Attempted to be sent by {displayName} on <0></0>' : 'Sent by {displayName} on <0></0>'}
            components={[
              <FancyTimeStamp 
                key="timestamp" 
                unixTime={timestamp / 1000} 
                displayTitle={false}
                {...timestampProps}
              />,
            ]}
            values={{ displayName: senderDisplayName }}
          />
        </span>
        <span className="message-details-id">{t('MessageDetailsView.messageID')}: {messageId}</span>
      </div>
    </div>
  );

  const renderReadByBlock = () => (
    <div className="message-details-block">
      <div
        className={`message-details-block-header ${(readByMembers.length) > 0 ? 'clickable' : ''}`}
        onClick={handleReadByHeaderClick}
      >
        <span className="message-details-list-title">
          <Trans
            i18nKey="MessageDetailsView.readByX"
            defaults="Read by <0>{numRead}</0>"
            components={[<span className="message-details-count" key="count" />]}
            values={{ numRead: readByMembers.length }}
          />
        </span>
      </div>
      {
        readByMembers.length > 0 &&  showReadBy && (
          <ContactList
            contacts={readByMembers}
            onSelectContact={(contactId) => onContactClick(contactId)}

          />
        )
      }
    </div>
  );

  const renderNotReadByBlock = () => (
    <div className="message-details-block no-bottom-border">
      <div
        className={`message-details-block-header ${(notReadByMembers.length) > 0 ? 'clickable' : ''}`}
        onClick={handleNotReadByHeaderClick}
      >
        <span className="message-details-list-title">
          <Trans
            i18nKey="MessageDetailsView.notReadByX"
            defaults="Not read by <0>{numUnread}</0>"
            components={[<span className="message-details-count" key="count" />]}
            values={{ numUnread: notReadByMembers.length }}
          />
        </span>
      </div>
      {
        notReadByMembers.length > 0 &&  showNotReadBy && (
          <ContactList
            contacts={notReadByMembers}
            onSelectContact={(contactId) => onContactClick(contactId)}

          />
        )
      }
    </div>
  );

  const renderRedactionBlock = () => {
    if (!message.isRedacted || !message.isRedacted()) return null;

    const redactionInfo = getRedactionInfo(message, members, client);

    // Handle case where redaction info is not available
    if (!redactionInfo) {
      // No redaction info available - show minimal UI
      return (
        <div className="message-details-block message-details-redaction-block">
          <div className="message-details-block-header">
            <span className="message-details-list-title">{t('ChatDetailsController.messageDeleted')}</span>
          </div>
          <div className="message-details-redaction-info">
            <div className="message-details-redaction-item">
              <span className="message-details-redaction-label">{t('ChatDetailsController.deletedBy')}:</span>
              <span className="message-details-redaction-value">{t('ChatDetailsController.deletedByUnknown')}</span>
            </div>
          </div>
        </div>
      );
    }

    const deleterName = redactionInfo.isCurrentUser
      ? t('MessageDetailsView.you')
      : `${redactionInfo.redactorName || t('ChatDetailsController.deletedByUnknown')}${needsDisambiguation(redactionInfo.redactorId) ? ` (${redactionInfo.redactorId})` : ''}`;
    const deletedAt = redactionInfo?.deletedAt;
    const deletionReason = redactionInfo?.reason;

    return (
      <div className="message-details-block message-details-redaction-block">
        <div className="message-details-block-header">
          <span className="message-details-list-title">{t('ChatDetailsController.messageDeleted')}</span>
        </div>
        <div className="message-details-redaction-info">
          <div className="message-details-redaction-item">
            <span className="message-details-redaction-label">{t('ChatDetailsController.deletedBy')}:</span>
            <span className="message-details-redaction-value">{deleterName}</span>
          </div>
          {deletedAt && (
            <div className="message-details-redaction-item">
              <span className="message-details-redaction-label">{t('ChatDetailsController.deletedOn')}:</span>
              <span className="message-details-redaction-value">
                <FancyTimeStamp
                  unixTime={deletedAt / 1000}
                  displayTitle={false}
                  {...timestampProps}
                />
              </span>
            </div>
          )}
          {deletionReason && (
            <div className="message-details-redaction-item">
              <span className="message-details-redaction-label">{t('ChatDetailsController.deletedReason')}:</span>
              <span className="message-details-redaction-value">{deletionReason}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDetailedErrorBlock = () => {
     if (!shouldRenderErrorBlock()) {
      return null;
    }

    const errorTitle = isFailedSend
      ? t('MessageDetailsView.sendingError')
      : t('ChatDetailsController.errorDetails');

    const errorMessage = isFailedSend
      ? (isCurrentUserMessage
        ? t('MessageDetailsView.couldNotBeSentByYou')
        : t('MessageDetailsView.couldNotBeSentByX', { displayName: senderDisplayName }))
      : (messageContent?.body || t('ChatView.unableToDecrypt'));
    
    return (
      <div className="message-details-block message-details-error-block">
        <div className="message-details-block-header">
          <span className="message-details-list-title">{errorTitle}</span>
        </div>
        <div className="message-details-error-info">
          <div className="message-details-error-item">
            <span className="message-details-error-value">
              {errorMessage}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <RightSidebar
      onClose={onClose}
      title={t('ChatDetailsController.messageDetails')}
    >
      <div className="message-details">
        {shouldRenderInfoBlock() && renderInfoBlock()}
        {shouldRenderReadByBlock() && renderReadByBlock()}
        {shouldRenderNotReadByBlock() && renderNotReadByBlock()}
        {shouldRenderRedactionBlock() && renderRedactionBlock()}
        {shouldRenderErrorBlock() && renderDetailedErrorBlock()}
      </div>
    </RightSidebar>
  );
};

MessageDetails.propTypes = {
  message: PropTypes.object.isRequired,
  members: PropTypes.array.isRequired,
  roomId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onContactClick: PropTypes.func.isRequired,
};

export default MessageDetails;
